import type { Theme } from '@mui/material';
import { chipClasses, svgIconClasses } from '@mui/material';
import type { /*Components, */ Palette, PaletteColor } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

import { xSmallSize } from '@/mui/components/constants';
import { SHAPE_ROUNDED, SHAPE_SQUARE } from '@/mui/constants';
import { ALPHA_300, ALPHA_400, ALPHA_600, ALPHA_700, ALPHA_800 } from '@/mui/constants/opacity';

import { blendColor } from '@/mui/helpers/color';

type Variant = {
  props: Record<string, unknown>;
  style: Record<string, unknown>;
};

export const CUSTOM_ATTRIBUTE_OVERLAY_EDIT = 'data-0';
export const CUSTOM_ATTRIBUTE_SHAPE = 'data-1';
export const CUSTOM_ATTRIBUTE_FULL_WIDTH = 'data-2';
export const CUSTOM_ATTRIBUTE_SINGLE_COLOR = 'data-3';

export default function MUIChip(theme: Theme) /*: Components['MuiChip']*/ {
  const variants = Object.keys(theme.palette)
    .filter((key) => !!(theme.palette[key as keyof Palette] as PaletteColor).contrastText)
    .reduce<Variant[]>((acc, key) => {
      const isSystemTag = key.startsWith('-tag');
      const mainColor = isSystemTag
        ? (theme.palette[key as keyof Palette] as PaletteColor).contrastText
        : (theme.palette[key as keyof Palette] as PaletteColor).main;

      return acc.concat(
        {
          props: {
            variant: 'text',
            color: key,
          },
          style: {
            color: mainColor,

            [`&.${chipClasses.clickable}:hover`]: {
              backgroundColor: `color-mix(in srgb, currentColor ${theme.palette.action.hoverOpacity * 100}%, transparent)`,
            },
          },
        },
        {
          props: {
            variant: 'outlined',
            color: key,
          },
          style: {
            color: mainColor,
            borderColor: 'currentColor',

            [`&.${chipClasses.clickable}:hover`]: {
              backgroundColor: `color-mix(in srgb, currentColor ${theme.palette.action.hoverOpacity * 100}%, transparent)`,
            },
          },
        },
      );
    }, []);

  return {
    defaultProps: {
      color: 'neutral',
      variant: 'filled',
    },
    variants: [
      {
        props: {
          [CUSTOM_ATTRIBUTE_OVERLAY_EDIT]: '',
        },
        style: {
          minWidth: xSmallSize * 2,
          '&:hover': {
            [`.${chipClasses.deleteIcon}`]: {
              opacity: 1,
              visibility: 'visible',
            },
          },
          [`.${chipClasses.deleteIcon}`]: {
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            color: theme.palette.text.primary,
            backgroundColor: alpha(theme.palette.background.paper, ALPHA_800),
            zIndex: 1,
            margin: 0,
            opacity: 0,
            visibility: 'hidden',
            transitionProperty: 'opacity, visibility',
            transitionDuration: '300ms',
            borderRadius: '100vw',

            '&:hover': {
              color: theme.palette.text.primary,
              backgroundColor: alpha(theme.palette.background.paper, ALPHA_800),
            },
          },
        },
      },
      {
        props: { variant: 'text' },
        style: {
          backgroundColor: 'transparent',

          [`.${chipClasses.label}`]: {
            color: 'currentColor',
          },

          [`.${chipClasses.deleteIcon}`]: {
            color: 'currentColor',
            opacity: ALPHA_700,

            '&:hover': {
              opacity: 1,
              color: 'currentColor',
            },
          },
        },
      },
      ...variants,
      {
        props: {
          [CUSTOM_ATTRIBUTE_FULL_WIDTH]: '',
        },
        style: {
          width: '100%',
        },
      },
      {
        props: {
          [CUSTOM_ATTRIBUTE_SHAPE]: SHAPE_SQUARE,
        },
        style: {
          borderRadius: 0,
        },
      },
      {
        props: {
          [CUSTOM_ATTRIBUTE_OVERLAY_EDIT]: '',
          [CUSTOM_ATTRIBUTE_SHAPE]: SHAPE_SQUARE,
        },
        style: {
          [`.${chipClasses.deleteIcon}`]: {
            borderRadius: 0,
          },
        },
      },
      {
        props: {
          [CUSTOM_ATTRIBUTE_SHAPE]: SHAPE_ROUNDED,
        },
        style: {
          borderRadius: theme.shape.borderRadius,
        },
      },
      {
        props: {
          [CUSTOM_ATTRIBUTE_OVERLAY_EDIT]: '',
          [CUSTOM_ATTRIBUTE_SHAPE]: SHAPE_ROUNDED,
        },
        style: {
          [`.${chipClasses.deleteIcon}`]: {
            borderRadius: theme.shape.borderRadius,
          },
        },
      },
      ...['filled', 'outlined', 'text'].map((variant) => {
        const customColor = 'var(--internal-custom-color, var(--theme-shades-900))';
        const bg = blendColor(variant === 'filled' ? ALPHA_400 : ALPHA_300, theme.palette.background.paper);

        return {
          props: {
            [CUSTOM_ATTRIBUTE_SINGLE_COLOR]: '',
            variant,
          },
          style: {
            color: customColor,
            borderColor: variant === 'outlined' ? 'currentColor' : 'transparent',
            backgroundColor:
              variant === 'filled' ? blendColor(ALPHA_300, theme.palette.background.paper) : 'transparent',

            [`&.${chipClasses.clickable}:hover`]: {
              backgroundColor: bg,
            },

            [` .${chipClasses.deleteIcon}`]: {
              color: 'currentColor',
              opacity: ALPHA_800,

              '&:hover': {
                color: 'currentColor',
                opacity: 1,
              },
            },
          },
        };
      }),
    ],
    styleOverrides: {
      root: {
        position: 'relative',

        [`&.${chipClasses.disabled}`]: {
          opacity: ALPHA_600,
        },
      },
      label: {
        lineHeight: 1,
      },
      sizeXSmall: {
        height: 20,
        fontSize: 10,
      },
      labelXSmall: {
        paddingLeft: '10px',
        paddingRight: '10px',
      },
      iconXSmall: {
        fontSize: 18,
      },
      deleteIconXSmall: {
        fontSize: 18,
      },
      sizeSmall: {
        height: 24,
        fontSize: 12,
      },
      labelSmall: {
        paddingLeft: '10px',
        paddingRight: '10px',
      },
      sizeMedium: {
        height: 28,
        fontSize: 13,
      },
      sizeLarge: {
        height: 32,
        fontSize: 14,
      },
      textNeutral: {
        color: theme.palette.shades[900],
        [`&.${chipClasses.clickable}:hover`]: {
          backgroundColor: theme.palette.action.hover,
        },
      },
      filledNeutral: {
        color: theme.palette.shades[900],
        backgroundColor: theme.palette.shades[300],

        [`&.${chipClasses.clickable}:hover`]: {
          backgroundColor: theme.palette.shades[400],
        },
      },
      outlinedNeutral: {
        color: theme.palette.shades[900],
        borderColor: theme.palette.shades[800],

        [`&.${chipClasses.clickable}:hover`]: {
          borderColor: theme.palette.shades[900],
          backgroundColor: theme.palette.action.hover,
        },
      },
      icon: {
        marginLeft: '2px',
        fontSize: 24,
        position: 'relative',
        top: 0,
        verticalAlign: 'middle',
      },
      iconLarge: {
        fontSize: 24,
      },
      iconSmall: {
        fontSize: 20,
      },
      avatarXSmall: {
        width: 18,
        height: 18,
        fontSize: 9,
        marginLeft: '2px',
      },
      avatarSmall: {
        width: 20,
        height: 20,
        fontSize: 10,
        marginLeft: '2px',
      },
      avatar: {
        width: 24,
        height: 24,
        marginLeft: '4px',
        fontSize: 12,
        lineHeight: 0,
      },
      avatarLarge: {
        width: 24,
        height: 24,
        fontSize: 12,
        marginLeft: '3px',
      },
      deleteIconSmall: {
        fontSize: '20px',
        marginRight: '2px',
        marginLeft: '-4px',
      },
      deleteIcon: {
        fontSize: '24px',
        cursor: 'default',
        marginRight: '2px',
        marginLeft: '-6px',
        verticalAlign: 'middle',

        [`&.${svgIconClasses.root}`]: {
          cursor: 'pointer',
        },

        [`& .${svgIconClasses.root}`]: {
          cursor: 'pointer',
        },
      },
      deleteIconColorNeutral: {
        color: alpha(theme.palette.shades[600], 0.7),

        '&:hover': {
          color: theme.palette.shades[600],
        },
      },
    },
    // variants: [],
  };
}
